import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * MenuDots component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconMenuDots extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.0003 6.53753C13.2933 6.53753 14.3415 5.48936 14.3415 4.19638C14.3415 2.90339 13.2933 1.85522 12.0003 1.85522C10.7073 1.85522 9.65918 2.90339 9.65918 4.19638C9.65918 5.48936 10.7073 6.53753 12.0003 6.53753Z"
          />
          <path
            d="M12.0003 14.3414C13.2933 14.3414 14.3415 13.2932 14.3415 12.0002C14.3415 10.7072 13.2933 9.65906 12.0003 9.65906C10.7073 9.65906 9.65918 10.7072 9.65918 12.0002C9.65918 13.2932 10.7073 14.3414 12.0003 14.3414Z"
          />
          <path
            d="M12.0003 22.1452C13.2933 22.1452 14.3415 21.097 14.3415 19.804C14.3415 18.5111 13.2933 17.4629 12.0003 17.4629C10.7073 17.4629 9.65918 18.5111 9.65918 19.804C9.65918 21.097 10.7073 22.1452 12.0003 22.1452Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-menu-dots') === undefined) {
  customElements.define('cat-icon-menu-dots', BlocksIconMenuDots);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-menu-dots': BlocksIconMenuDots;
  }
}
